import { useCallback, useMemo, useRef, useState } from "react";
import { ButtonGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Button from "../../common/components/Button";
import CustomForm from "../../common/components/CustomForm";
import DataTable from "../../common/components/DataTable";
import DefaultButton from "../../common/components/DefaultButton";
import { Col, Row } from "../../common/components/Grid";
import Label from "../../common/components/Label";
import { SettingsDisplay, useSettingsContext } from "../../common/components/SettingsPanels";
import { SortDialog } from "../../common/components/Sort";
import TextInput from "../../common/components/TextInput";
import { groupBy } from "../../common/utils/arrayUtilities";
import { deepClone, deepEquals, writeField } from "../../common/utils/objectUtilities";
import { dynamicFilter, dynamicSort } from "../../common/utils/tableUtilities";
import PropTypes from 'prop-types';
import InfiniteScroll from "../../common/components/InfiniteScroll";

/**
 * A function to group entries
 * @param {*} entries The entries to be grouped
 * @param {*} initialGroupField A field to use to create the initial groupings - the better this matches the grouping the better the performance
 * @param {*} excludedFields Fields to exclude from comparison when doing a equality checks
 * @returns An array of arrays - each element array is an array of entries representing a group
 */
const groupEntries = (entries, initialGroupField, excludedFields = []) => {
    let groupedEntries = groupBy(entries, initialGroupField);
    let groups = Object.entries(groupedEntries).flatMap(([field, value]) => {
        let groups = [];
        value.forEach(e => {
            let idx = groups.findIndex(g => Object.keys(g[0]).every(key => excludedFields.includes(key) || deepEquals(g[0][key], e[key])))
            if (idx === -1) {
                groups.push([e]);
            } else {
                groups[idx].push(e);
            }
        })
        return groups;
    });
    return groups;
}

/**
 * The button to set the editor to editing a new entry
 * @component
 */
const NewEntryButton = ({ selected, onClick }) => {
    const { pushPanel } = useSettingsContext();
    return <DefaultButton className="m-2" kind="add" variant={selected ? "primary" : "outline-primary"} onClick={() => { pushPanel(1); onClick(); }}></DefaultButton>
}
NewEntryButton.propTypes = {
    /** Property that renders the button as the current selection */
    selected: PropTypes.bool,
    /** Function to call when the button is clicked */
    onClick: PropTypes.func
}

/**
 * A button used to represent a single entry - used when grouping is disabled.
 * @component
 */
const SingleEntryButton = ({entry, groupingCtrls, makeLabel, selected, onClick}) => {
    const { pushPanel } = useSettingsContext();
    const rowDef = useMemo(() => [{ borderless: true, rows: [groupingCtrls.map(ctrl => ({ ...ctrl, readonly: true}))]}], [groupingCtrls]);

    return <Button className="m-2" variant={selected ? "dark" : "outline-dark"} onClick={() => { pushPanel(1, () => onClick()); onClick(entry); }}>
        <Row>
            <Col md={4} className="text-left">
                {makeLabel(entry)}
            </Col>
            <Col md={8} className="text-left">
                <CustomForm def={rowDef} data={entry} readonly={true} hideLabel={true}></CustomForm>
            </Col>
        </Row>
    </Button>
}
SingleEntryButton.propTypes = {
    /** The entry the button represents */
    entry: PropTypes.object,
    /** The controls for the grouped fields (an array of custom control definitions) */
    groupingCtrls: PropTypes.object,
    /** A function which accepts the entry and returns the text label representing that entry */
    makeLabel: PropTypes.func,
    /** A boolean designating if the current entry this button represents is currently selected */
    selected: PropTypes.bool,
    /** The function to call when the button is clicked */
    onClick: PropTypes.func
}

/**
 * A button used to represent a group of entries+
 * @component
 */
const GroupedEntryButton = ({ entry, pkey, groupingCtrls, makeLabel, selected, onClick}) => {
    const { pushPanel } = useSettingsContext();
    const rowDef = useMemo(() => [{ borderless: true, rows: [groupingCtrls.map(ctrl => ({ ...ctrl, readonly: true }))]}], [groupingCtrls]);

    return <Button className="m-2" variant={selected ? "dark" : "outline-dark"} style={{ whiteSpace: 'normal' }} onClick={() => { pushPanel(1, () => onClick()); onClick(entry); }}>
        <Row>
            <Col md={4} className="text-left">
                {makeLabel(entry[0])}
            </Col>
            <Col md={8} className="text-left">
                {entry.map((e, i) => <CustomForm key={e[pkey]} def={rowDef} data={e} readonly={true} hideLabel={true} className={ i === entry.length - 1 ? null : "mb-2" }></CustomForm>)}
            </Col>
        </Row>
    </Button> 
}
GroupedEntryButton.propTypes = {
    /** The entry group the button represents */
    entry: PropTypes.object,
    /** The primary key field name for the entries */
    pkey: PropTypes.string,
    /** The controls for the grouped fields (an array of custom control definitions) */
    groupingCtrls: PropTypes.any,
    /** A function which accepts the entry and returns the text label representing that entry */
    makeLabel: PropTypes.func,
    /** A boolean designating if the current entry group this button represents is currently selected */
    selected: PropTypes.bool,
    /** The function to call when the button is clicked */
    onClick: PropTypes.func
}

/**
 * The grouping toggle button - should entries be grouped together when they match apart from grouping / excluded fields or ungrouped.
 * @component
 */
const GroupingButton = ({ grouped, onClick }) => {
    return <DefaultButton className="mx-2" kind={grouped ? "ungroup" : "group"} onClick={onClick}></DefaultButton>
}
GroupingButton.propTypes = {
    /** Grouping currently enabled iff true */
    grouped: PropTypes.bool,
    /** function to call when the button is clicked */
    onClick: PropTypes.func
}

/**
 * This component creates a two-panel settings editor where the entries to be edited can be grouped using some subset of fields.
 * Grouping can be toggled and when grouped a single entry update can update multiple entries.
 * @component
 */
const GroupedSettings = ({ data, defaultEntry = {}, groupingCtrls, initialGroupingField, groupingExclusions, uuid, pkey = 'internalid', headerControls: inputHeaderControls, title, guide, makeLabel = e => e.name, defaultSort, sortFields, canAdd, canUpdate, canRemove, onAdd, onUpdate, onRemove, Details, onClose }) => {
    const { t } = useTranslation();
    const [showGrouped, setShowGrouped] = useState(groupingCtrls?.length > 0);
    const [selectedEntry, setSelectedEntry] = useState();
    const [sort, setSort] = useState(defaultSort);
    const [showSortDialog, setShowSortDialog] = useState(false);
    const [query, setQuery] = useState();
    const [newEntry, setNewEntry] = useState(deepClone(defaultEntry));
    const [newEntryGroup, setNewEntryGroup] = useState([]);
    const formRef = useRef();

    const groupedEntries = useMemo(() => {
        let sortedEntries = [...data].sort(dynamicSort(sort));
        return showGrouped ? groupEntries(sortedEntries, initialGroupingField, groupingExclusions) : sortedEntries;
    }, [showGrouped, data, initialGroupingField, groupingExclusions, sort]);

    const displayEntries = useMemo(() => {
        return query ? dynamicFilter(groupedEntries, query) : groupedEntries;
    }, [groupedEntries, query]);

    const isNew = !selectedEntry || !data.find(d => d[pkey] === selectedEntry);
    const entry = isNew ? newEntry : data.find(d => d[pkey] === selectedEntry);
    const entryGroup = useMemo(() => (showGrouped && !isNew) ? groupedEntries.find(group => group.some(p => p[pkey] === selectedEntry )) : newEntryGroup, [showGrouped, selectedEntry, groupedEntries, pkey, newEntryGroup, isNew]);

    const clearData = useCallback(() => {
        setSelectedEntry();
        setNewEntry(deepClone(defaultEntry))
        setNewEntryGroup([]);
    }, [defaultEntry]);

    const handleUpdate = field => value => {
        if (isNew) {
            setNewEntry(entry => {
                const newEntry = { ...entry };
                writeField(newEntry, field, value);
                return newEntry;
            });
        } else if (showGrouped) {
            entryGroup.forEach(entry => {
                const newEntry = { [pkey]: entry[pkey] };
                writeField(newEntry, field, value);
                onUpdate(newEntry);
            })
        } else {
            const newEntry = { [pkey]: entry[pkey] };
            writeField(newEntry, field, value);
            onUpdate(newEntry);
        }
    }

    const handleCreate = () => {
        if (formRef.current.checkValidity()) {
            if (showGrouped) {
                newEntryGroup.forEach(({ ...props }) => {
                    let data = { ...entry, ...props };
                    delete data[pkey];
                    onAdd(data);
                })
            } else {
                onAdd(entry);
            }
            clearData();
        }
    }

    const handleRemove = entry => {
        clearData();
        if (showGrouped) {
            entryGroup.forEach(e => onRemove(e));
        } else {
            onRemove(entry);
        }
    }

    const handleGroupAdd = useCallback(details => {
        const newGroup = { ...entry, ...details };
        delete newGroup[pkey];
        if (isNew) {
            setNewEntryGroup(groups => [...groups, newGroup])
         } else {
            onAdd(newGroup)
         }
    }, [isNew, onAdd, entry, pkey]);

    const handleGroupRemove = useCallback(details => {
        if (isNew) {
            setNewEntryGroup(groups => groups.filter(g => !deepEquals(g, details)))
        } else {
            if (showGrouped && selectedEntry === details[pkey]) {
                if (entryGroup.length > 1) {
                    setSelectedEntry(entryGroup[1][pkey]);
                } else {
                    clearData();
                }
            }
            onRemove(details)
        }
    }, [isNew, onRemove, entryGroup, pkey, clearData, showGrouped, selectedEntry]);

    const invalid = () => {
        if (showGrouped && (!entryGroup || entryGroup.length < 1)) {
            return true
        }
        if (canAdd) {
            if (showGrouped) {
                return newEntryGroup.some(({ ...props }) => {
                    return !canAdd({ ...entry, ...props });
                })
            } else {
                return !canAdd(entry);
            }
        }
        return false;
    }

    const headerControls = groupingCtrls?.length > 0 ? <Row>
                <Col></Col>
                { inputHeaderControls && <Col xs="auto">{inputHeaderControls}</Col> }
                <Col xs="auto"><GroupingButton grouped={showGrouped} onClick={() => setShowGrouped(grouped => !grouped)}></GroupingButton></Col>
            </Row> : null;
    const groupCtrl = showGrouped ? 
        <DataTable value={entryGroup} columns={groupingCtrls} onAdd={handleGroupAdd} onRemove={handleGroupRemove} canAdd={details => canAdd({ ...entry, ...details })} canRemove={entry => (isNew || entryGroup.length > 1) && canRemove(entry)}></DataTable>
        : <CustomForm data={entry} onChange={delta => Object.entries(delta).forEach(([field, value]) => handleUpdate(field)(value))} def={[{borderless: true, rows: [groupingCtrls]}]}></CustomForm>

    return <>
        <SortDialog show={showSortDialog} defaultOrder={defaultSort} sort={sort} fields={sortFields} title={t('userSort')} onClose={newSort => { if (newSort) { setSort(newSort); } setShowSortDialog(false); }} placeholder={t('sort')}></SortDialog>
        <SettingsDisplay uuid={uuid} title={title} guide={guide} onClose={onClose} defaultSizes={[25, 75]} headerControls={headerControls} controls={headerControls} minSize={350}>
            <div className="d-flex flex-column w-100 h-100">
                <Row>
                    <Col>
                        <TextInput value={query} live={true} hideLabel={true} placeholder={t('filter', { count: 1 })} onChange={setQuery}
                            prependCtrl={
                                <ButtonGroup>
                                    <DefaultButton kind="sort" onClick={() => setShowSortDialog(true)} hideLabel={true}></DefaultButton>
                                </ButtonGroup>
                            }>
                        </TextInput>
                    </Col>
                </Row>
                {onAdd && <NewEntryButton selected={isNew} onClick={clearData}></NewEntryButton>}
                <Row className="mx-2 d-none d-md-flex">
                    <Col md={4}></Col>
                    <Col md={8} className="px-2 text-start">
                        <Row>
                            {groupingCtrls.map(ctrl => <Col key={ctrl.label} style={{ fontWeight: 'bold' }}><Label text={ctrl.label}></Label></Col>)}
                        </Row>
                    </Col>
                </Row>
                <InfiniteScroll data={displayEntries} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                    renderItem={e => {
                        if (showGrouped && Array.isArray(e)) {
                            return <GroupedEntryButton key={e?.[0]?.[pkey]} entry={e} pkey={pkey} groupingCtrls={groupingCtrls} makeLabel={makeLabel} onClick={e => setSelectedEntry(e?.[0]?.[pkey])} selected={!isNew && deepEquals(e, entryGroup)}></GroupedEntryButton>
                        }
                        return <SingleEntryButton key={e?.[pkey]} entry={e} groupingCtrls={groupingCtrls} makeLabel={makeLabel} onClick={e => setSelectedEntry(e?.[pkey])} selected={!isNew && selectedEntry=== e[pkey]}></SingleEntryButton>
                }}
                ></InfiniteScroll>
            </div>
            <div className="ml-2 mr-0 h-100 d-flex flex-column">
                <Row>
                    <Col className="ml-1">
                        {isNew ? <DefaultButton kind="clear" onClick={() => clearData()}></DefaultButton> : <DefaultButton kind="delete" disabled={!onRemove || entryGroup?.length > 1 || (canRemove && !canRemove(entry))} onClick={() => handleRemove(entry)}></DefaultButton>}
                    </Col>
                    <Col className="text-right">
                        {isNew ? <DefaultButton kind="add" disabled={invalid()} onClick={() => handleCreate()}></DefaultButton> : null}
                    </Col>
                </Row>
                <div className="mt-2" style={{ flex: 1, overflowY: 'auto' }}>
                    <Details ref={formRef} value={entry} groupCtrl={groupCtrl} grouped={showGrouped} disabled={!isNew && (!onUpdate || (canUpdate && entry[pkey] && !canUpdate(entry)))} onChange={handleUpdate}></Details>
                </div>
            </div>
        </SettingsDisplay>
    </>
}
GroupedSettings.propTypes = {
    /** The set of entries to be displayed / available for editing */
    data: PropTypes.any,
    /** A template representing the defaults for a new entry */
    defaultEntry: PropTypes.object,
    /** The controls for the grouped fields (an array of custom control definitions) */
    groupingCtrls: PropTypes.any,
    /** A field to use to create the initial groupings - the better this matches the grouping the better the performance */
    initialGroupingField: PropTypes.any,
    /** An array of fields to exclude from grouping - you should exclude fields used by the groupingCtrls */
    groupingExclusions: PropTypes.arrayOf(PropTypes.string),
    /** The uuid used to uniquely identify the settings panel - if set used to show status */
    uuid: PropTypes.string,
    /** The string name of the primary key field to be used to uniquely identify entries */
    pkey: PropTypes.string.isRequired,
    /** A function to return the label for a given entry */
    makeLabel: PropTypes.func,
    /** The default sort to be applied to the list of entries */
    defaultSort: PropTypes.arrayOf(PropTypes.string),
    /** An object which is a field to name map for the purposes of the sorting UI */
    sortFields: PropTypes.object,
    /** A function which returns true if a given entry can be created - false otherwise */
    canAdd: PropTypes.func,
    /** A function which returns true if a given entry can be updated - false otherwise */
    canUpdate: PropTypes.func,
    /** A function which return true if a given entry can be removed - false otherwise */
    canRemove: PropTypes.func,
    /** A function to call to add a new entry - takes the entry to add as its only argument */
    onAdd: PropTypes.func,
    /** A function to call to update an existing entry - takes a delta object as its only argument */
    onUpdate: PropTypes.func,
    /** A function to call to remove an entry - takes the entry as its only argument */
    onRemove: PropTypes.func,
    /** A control to redner the details of a given entry - must accept a group control to render the controls for updating the grouped fields  */
    Details: PropTypes.any,
    /** A function to be called when the settings panel is closed */
    onClose: PropTypes.func
}

export default GroupedSettings;